import React, { useState } from 'react';
import { bool, func, oneOfType, string } from 'prop-types';
import truncate from 'lodash/truncate';
import classNames from 'classnames';

import { FormattedMessage, FormattedDate } from '../../../util/reactIntl';
import { richText } from '../../../util/richText';
import { ensureUser, ensureCurrentUser } from '../../../util/data';
import { propTypes } from '../../../util/types';

import { NamedLink, InlineTextButton } from '../../../components';
import { profilePageLinkProps } from '../../../util/profileSeoUrl';
import { trackListingEvent } from '../../../util/ecommerceEvents';

import css from './UserCard.module.css';

const BIO_COLLAPSED_LENGTH = 170;
const MIN_LENGTH_FOR_LONG_WORDS = 20;

const truncated = s =>
  truncate(s, {
    length: BIO_COLLAPSED_LENGTH,
    separator: /\s|,|\.|:|;/,
    omission: '…',
  });

const ExpandableBio = props => {
  const [expand, setExpand] = useState(false);
  const { className, bio } = props;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });
  const truncatedBio = richText(truncated(bio), {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
    breakChars: '/',
  });
  const showMore = (
    <InlineTextButton rootClassName={css.showMore} onClick={() => setExpand(true)}>
      <FormattedMessage id="UserCard.showFullBioLink" />
    </InlineTextButton>
  );
  return (
    <p className={className}>
      {expand ? bioWithLinks : truncatedBio}
      {bio.length >= BIO_COLLAPSED_LENGTH && !expand ? showMore : null}
    </p>
  );
};

ExpandableBio.defaultProps = { className: null };
ExpandableBio.propTypes = { className: string, bio: string.isRequired };

const VerifiedCheck = () => (
  <svg
    className={css.verifiedCheck}
    viewBox="0 0 20 20"
    fill="currentColor"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z"
    />
  </svg>
);

const MailGlyph = () => (
  <svg
    className={css.btnIcon}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
    />
  </svg>
);

const PhoneGlyph = () => (
  <svg
    className={css.btnIcon}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth="1.5"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"
    />
  </svg>
);

const WhatsAppGlyph = () => (
  <svg
    className={css.btnIcon}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M16.75 13.96c.25.13.41.2.46.3.06.11.04.61-.21 1.18-.2.56-1.24 1.1-1.7 1.12-.46.02-.47.36-2.96-.73-2.49-1.09-3.99-3.75-4.11-3.92-.12-.17-.96-1.38-.92-2.61.05-1.22.69-1.8.95-2.04.24-.26.51-.29.68-.26h.47c.15 0 .36-.06.55.45l.69 1.87c.06.13.1.28.01.44l-.27.41-.39.42c-.12.12-.26.25-.12.5.12.26.62 1.09 1.32 1.78.91.88 1.71 1.17 1.95 1.3.24.14.39.12.54-.04l.81-.94c.19-.25.35-.19.58-.11l1.67.88M12 2a10 10 0 0 1 10 10 10 10 0 0 1-10 10c-1.97 0-3.8-.57-5.35-1.55L2 22l1.55-4.65A9.969 9.969 0 0 1 2 12 10 10 0 0 1 12 2m0 2a8 8 0 0 0-8 8c0 1.72.54 3.31 1.46 4.61L4.5 19.5l2.89-.96A7.95 7.95 0 0 0 12 20a8 8 0 0 0 8-8 8 8 0 0 0-8-8z" />
  </svg>
);

const UserCard = props => {
  const {
    rootClassName,
    className,
    user,
    currentUser,
    email,
    phoneNumber,
    whatsAppNumber,
  } = props;

  const [showEmail, setShowEmail] = useState(false);
  const [showPhone, setShowPhone] = useState(false);
  const [showWhatsapp, setShowWhatsapp] = useState(false);

  const userIsCurrentUser = user && user.type === 'currentUser';
  const ensuredUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const isCurrentUser =
    ensuredUser.id && ensuredCurrentUser.id && ensuredUser.id.uuid === ensuredCurrentUser.id.uuid;
  const { displayName, bio, publicData } = ensuredUser.attributes.profile;
  const { userType } = publicData || {};
  const isBusiness = userType === 'business';
  const createdAt = ensuredUser.attributes.createdAt;

  const initial = (displayName || '').trim().charAt(0).toUpperCase() || '?';
  const classes = classNames(rootClassName || css.root, className);

  return (
    <div className={classes}>
      <div className={css.header}>
        <div className={css.avatar} aria-hidden="true">{initial}</div>
        <div className={css.headerText}>
          <div className={css.nameRow}>
            <span className={css.name}>{displayName}</span>
            {isBusiness ? <VerifiedCheck /> : null}
          </div>
          <span className={css.type}>
            <FormattedMessage
              id={
                isBusiness
                  ? 'OrderPanel.authorTypeBusiness'
                  : 'OrderPanel.authorTypePrivate'
              }
            />
          </span>
          {bio ? <ExpandableBio className={css.bio} bio={bio} /> : null}
        </div>
      </div>

      {createdAt ? (
        <p className={css.meta}>
          <FormattedMessage
            id="UserCard.memberSince"
            values={{
              date: <FormattedDate value={createdAt} year="numeric" month="short" />,
            }}
          />
        </p>
      ) : null}

      <div className={css.actions}>
        {ensuredUser.id && !isCurrentUser ? (
          <NamedLink className={css.primaryBtn} {...profilePageLinkProps(ensuredUser)}>
            <FormattedMessage id="UserCard.viewProfileLink" />
          </NamedLink>
        ) : null}

        {email ? (
          showEmail ? (
            <a href={`mailto:${email}`} className={css.outlineBtn}>
              <MailGlyph />
              {email}
            </a>
          ) : (
            <button
              type="button"
              className={css.outlineBtn}
              onClick={() => {
                // P25009-482 BR-7 — measured, so the layout change can be judged.
                trackListingEvent('contact_email', { user_id: user?.id?.uuid });
                setShowEmail(true);
              }}
            >
              <MailGlyph />
              Visa e-post
            </button>
          )
        ) : null}

        {phoneNumber ? (
          showPhone ? (
            <a href={`tel:${phoneNumber}`} className={css.outlineBtn}>
              <PhoneGlyph />
              {phoneNumber}
            </a>
          ) : (
            <button
              type="button"
              className={css.outlineBtn}
              onClick={() => {
                trackListingEvent('contact_phone', { user_id: user?.id?.uuid });
                setShowPhone(true);
              }}
            >
              <PhoneGlyph />
              Visa telefonnummer
            </button>
          )
        ) : null}

        {whatsAppNumber ? (
          showWhatsapp ? (
            <a
              href={`https://wa.me/${whatsAppNumber.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className={css.outlineBtn}
            >
              <WhatsAppGlyph />
              {whatsAppNumber}
            </a>
          ) : (
            <button
              type="button"
              className={css.outlineBtn}
              onClick={() => setShowWhatsapp(true)}
            >
              <WhatsAppGlyph />
              Visa WhatsApp
            </button>
          )
        ) : null}
      </div>
    </div>
  );
};

UserCard.defaultProps = {
  rootClassName: null,
  className: null,
  user: null,
  currentUser: null,
  showContact: true,
};

UserCard.propTypes = {
  rootClassName: string,
  className: string,
  user: oneOfType([propTypes.user, propTypes.currentUser]),
  currentUser: propTypes.currentUser,
  onContactUser: func,
  showContact: bool,
  email: string,
  phoneNumber: string,
  whatsAppNumber: string,
};

export default UserCard;
